/**
 * League › Rules — the destination every `§` in this app has been pointing at.
 *
 * Twenty-odd citations were reserved across FAAB, Trades, the Calculator and Draft Order and left
 * deliberately inert, because a mark that opens nothing is better than a mark that opens the wrong
 * thing. This is what they open.
 *
 * THREE THINGS THIS PAGE REFUSES TO DO, each of them a bug the design pass shipped first:
 *
 *   IT NEVER WRITES A COUNT DOWN. A group's badge is `rulesIn(id).length` — the same call that
 *   fills the pane. A badge that disagrees with its pane is the failure `domain/ruleGroups.ts`
 *   exists to make impossible.
 *
 *   IT NEVER PUTS A RULE IN ONE GROUP. Membership is multi-valued: 48 of the 84 in-force rules
 *   carry more than one topic, and assigning by priority is what hid 2020-II-1 — the rule defining
 *   how draft slots are chosen — from the Draft group behind `playoffs`.
 *
 *   IT NEVER RETYPES A RULE. Text, title, summary and lineage all come from `rulebook.json`;
 *   `supersedes` is the only source for "Replaces …". Six paraphrased rules and one invented title
 *   is what happens otherwise.
 *
 * SUPERSEDED RULES OPEN. Three reserved ids point at rules no longer in force, and 2020-I-5 is the
 * case that settles it: it carries the ₣50 rename-refusal fine and the deficit-carries-forward
 * clause, and the amendment that superseded it only lowered a blowout threshold from 100 to 75,
 * restating nothing else. The operative text is in the superseded rule. So a retired rule is
 * reachable, readable and clearly marked — it is simply not counted.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { allManagers } from '../domain/identity.js';
import { type RulebookEntry, ruleEntry } from '../domain/rulebook.js';
import {
  LEAD_GROUPS, OTHER_GROUPS, type RuleGroup, countIn, groupOwning, lineageOf, ruleGroup,
  rulesIn, yearsIn,
} from '../domain/ruleGroups.js';
import { codifyDraft } from '../domain/interpretation.js';
import {
  type Ordering, STATUS_FILTERS, type StatusFilter, emptyStateFor, railFor, rulesForPane,
  scopeNote, searching, statusCounts,
} from '../domain/rulesPane.js';
import { atLeast, type InterpretationDoc, type LeagueStore, type Role } from '../store/types.js';
import { useRulebook } from './useRulebook.js';
import { InterpretationPanel } from './Interpretation.js';
import { Linkify } from './RuleText.js';
import { GridPane } from './RulesGrid.js';
import { ScoringPane } from './RulesScoring.js';
import last2025 from '../data/season-2025.json';

const SEASON = 2026;
const LAST_FINISH: string[] = (last2025 as { standings: { gm: string }[] }).standings
  .map((s) => s.gm);

/** Where the rail starts. The Grid is the thing most people arrive wanting. */
const DEFAULT_GROUP = 'grid';

export interface RulesProps {
  gm: string;
  role: Role;
  store: LeagueStore;
  /** A rule id from the route — `#/league/rules/2015-II-2`. Selects its group and opens it. */
  focusRule?: string;
  /**
   * The ordering, from the route. `#/league/rules/byyear` used to be a tab of its own and is now
   * this — see `route.ts`, which still resolves the old address because it is in Discord links.
   */
  order?: Ordering;
}

export function Rules({ gm, role, store, focusRule, order: orderProp }: RulesProps) {
  const [group, setGroup] = useState<string>(DEFAULT_GROUP);
  /*
   * THE THREE CONTROLS THE HANDOFF ADDS. Status was not a control at all before — the pane showed
   * the 84 in force and the other 48 were reachable only by knowing an id and typing it into the
   * URL. `By year` was a third tab, which asked "which list?" when the only question anybody has
   * is "which rule?"; it is an ordering now and the rail swaps underneath it.
   */
  const [status, setStatus] = useState<StatusFilter>('active');
  const [order, setOrder] = useState<Ordering>(orderProp ?? 'topic');
  const [q, setQ] = useState('');
  const [openRule, setOpenRule] = useState<string | null>(null);
  const [year, setYear] = useState<number | 'all'>('all');
  const [interps, setInterps] = useState<InterpretationDoc[]>([]);
  const book = useRulebook(store, SEASON);
  const [asking, setAsking] = useState<string | null>(null);
  const [question, setQuestion] = useState('');
  const paneRef = useRef<HTMLDivElement>(null);

  /*
   * ARRIVING ON A CITATION.
   *
   * Three things have to happen together and in this order: select the group that holds the rule,
   * open the rule, and clear the year filter — a `§` that lands on a group filtered to 2019 shows
   * an empty pane and looks broken. The scroll is deferred a frame because the row does not exist
   * in the DOM until the group has re-rendered.
   */
  useEffect(() => {
    if (!focusRule) return undefined;
    const owner = groupOwning(focusRule);
    if (!owner) return undefined;
    setGroup(owner.id);
    setYear('all');
    setOpenRule(focusRule);
    const id = window.requestAnimationFrame(() => {
      document.getElementById(focusRule)?.scrollIntoView({ block: 'start', behavior: 'smooth' });
    });
    return () => window.cancelAnimationFrame(id);
  }, [focusRule]);

  /*
   * Interpretations, loaded once for the whole page. They belong ON THE RULE rather than on a
   * screen of their own: somebody arrives at 2025 P05 with the question, and an answer filed under
   * "governance" is an answer nobody finds.
   */
  useEffect(() => {
    let live = true;
    const load = () => store.interpretations(SEASON).then((x) => { if (live) setInterps(x); });
    void load();
    const off = store.subscribe(SEASON, (what) => { if (what === 'interpretations') void load(); });
    return () => { live = false; off(); };
  }, [store]);

  const byRule = useMemo(() => {
    const m = new Map<string, InterpretationDoc[]>();
    for (const i of interps) m.set(i.ruleId, [...(m.get(i.ruleId) ?? []), i]);
    return m;
  }, [interps]);

  /*
   * Who sits on an interpretation panel, derived from the season rather than chosen. `conflicted`
   * is deliberately empty: the app cannot know who stands to benefit from a reading of an
   * arbitrary rule, and guessing would be worse than asking. A commissioner marks a recusal on the
   * panel itself.
   */
  const panelInput = useMemo(() => ({
    commissioners: allManagers().filter((m) => m.role === 'commissioner').map((m) => m.id),
    conflicted: [],
    finishOrder: LAST_FINISH,
    lotteryWinner: null,
  }), []);

  const current = ruleGroup(group) ?? ruleGroup(DEFAULT_GROUP)!;
  const inForce = rulesIn(current.id);
  const years = yearsIn(current.id);

  /*
   * WHEN THE NEW PANE TAKES OVER. Searching, ordering by year, or looking at anything other than
   * what is in force are all questions the old group pane cannot answer — it is built from
   * `rulesIn`, which only ever returns active rules of one group. The Grid and the Scoring table
   * stay as they are underneath: they are not lists of rules, they are one rule drawn as a table,
   * and there is no such thing as a superseded view of them.
   */
  const isSearch = searching(q);
  const filtered = isSearch || order === 'year' || status !== 'active';
  /*
   * THE EFFECTIVE CORPUS, not the bundled one. A rule the committee made permanent last night is
   * permanent on this pane tonight — which it was not: `RULES` is generated data, so two rules
   * re-voted through on 26 August went on reading "provisional" two tabs from the docket that had
   * just passed them.
   */
  const paneQuery = {
    status, order, q, rules: book.rules,
    ...(order === 'topic' ? { topic: current.id } : {}),
    ...(order === 'year' && year !== 'all' ? { year: year as number } : {}),
  };
  const paneRules = filtered ? rulesForPane(paneQuery) : rulesIn(current.id, { year });
  const shown = paneRules;
  const empty = filtered ? emptyStateFor(paneQuery) : null;
  const counts = statusCounts(book.rules);
  const rail = railFor(order, status, book.rules);

  /*
   * THE ONE RULE THAT IS NOT IN THE PANE.
   *
   * A `§` can point at a superseded rule, and a lineage line always does. Neither is in any
   * group's list, so the row is appended to whichever group owns it rather than being unreachable
   * — visible, marked with its status, and not counted.
   */
  const focused = openRule ? ruleEntry(openRule) : undefined;
  const retiredRow = focused && focused.status !== 'active'
    && groupOwning(focused.id)?.id === current.id ? focused : undefined;

  const commish = atLeast(role, 'commissioner');

  return (
    <div className="app wide rulespage">
      <div className="pagehead">
        <h1>Rules</h1>
        <p className="sub">
          {book.rules.filter((r) => r.status === 'active').length} rules in force.
          {' '}Every citation in the app opens here.
        </p>
      </div>

      {/*
        SEARCH IS THE PRIMARY AFFORDANCE, above the filters rather than inside them. Somebody
        arriving at a rulebook overwhelmingly wants one rule, and the fastest route to it is a
        word they remember from it — not a topic they have to guess the app's name for.
      */}
      <div className="rulefind">
        <input className="rulesearch" type="search" value={q}
          onChange={(e) => { setQ(e.target.value); setOpenRule(null); }}
          placeholder={`Search all ${counts.all} rules — keeper, IR, waiver, 2019…`}
          aria-label="Search the rulebook" />
        {isSearch && (
          <span className="rulefound mut">
            {shown.length} {shown.length === 1 ? 'result' : 'results'}
          </span>
        )}
      </div>

      <div className="rulefilters">
        <div className="rulechips" role="group" aria-label="Rule status">
          {STATUS_FILTERS.map((f) => (
            <button key={f.id} type="button" aria-pressed={status === f.id}
              className={`rulechip ${status === f.id ? 'on' : ''}`}
              onClick={() => { setStatus(f.id); setOpenRule(null); }}>
              {f.label} <i>{counts[f.id]}</i>
            </button>
          ))}
        </div>
        <div className="panes small ruleorder" role="group" aria-label="Ordering">
          {(['topic', 'year'] as Ordering[]).map((o) => (
            <button key={o} type="button" aria-pressed={order === o}
              className={`pane ${order === o ? 'on' : ''}`}
              onClick={() => { setOrder(o); setYear('all'); setOpenRule(null); }}>
              By {o}
            </button>
          ))}
        </div>
        <span className="rulescope mut">{scopeNote(status, q)}</span>
      </div>

      <div className="ruleslayout">
        {/* -- the rail ------------------------------------------------------------------- */}
        <nav className="rulerail" aria-label={order === 'year' ? 'Years' : 'Rule topics'}>
          {/*
            THE RAIL IS THE ORDERING, and swapping its contents is the whole of what `By year`
            used to be as a tab. A row whose count falls to zero under the live filter STAYS —
            "Trades 0" while Rescinded is selected answers "has a trades rule ever been
            rescinded" without anybody having to click, and dropping the row takes that away.
          */}
          {order === 'year' ? (
            <>
              <p className="railcap">Years</p>
              <ul className="raillead">
                {rail.map((row) => (
                  <li key={row.id}>
                    <button type="button"
                      className={`railrow plain ${String(year) === row.id ? 'on' : ''} `
                        + `${row.count === 0 ? 'none' : ''}`}
                      aria-current={String(year) === row.id ? 'true' : undefined}
                      onClick={() => { setYear(Number(row.id)); setOpenRule(null); }}>
                      <span className="raillabel">{row.label}</span>
                      <span className="railcount">{row.count}</span>
                    </button>
                  </li>
                ))}
              </ul>
              {year !== 'all' && (
                <button type="button" className="linkbtn tiny railall"
                  onClick={() => setYear('all')}>Every year</button>
              )}
            </>
          ) : (
          <>
          <p className="railcap">Key topics</p>
          <ul className="raillead">
            {LEAD_GROUPS.map((g) => (
              <RailRow key={g.id} group={g} on={g.id === current.id}
                count={rail.find((r) => r.id === g.id)?.count}
                onPick={() => { setGroup(g.id); setOpenRule(null); setYear('all'); }} />
            ))}
          </ul>
          <ul className="railrest">
            {OTHER_GROUPS.map((g) => (
              <RailRow key={g.id} group={g} on={g.id === current.id} plain
                count={rail.find((r) => r.id === g.id)?.count}
                onPick={() => { setGroup(g.id); setOpenRule(null); setYear('all'); }} />
            ))}
          </ul>

          {/* The Grid has no list to filter, so it has no year filter either. */}
          {current.kind !== 'grid' && years.length > 1 && (
            <label className="railyear">
              <span className="mut">Year</span>
              <select value={String(year)}
                onChange={(e) => setYear(e.target.value === 'all' ? 'all' : Number(e.target.value))}>
                <option value="all">Every year</option>
                {years.map((y) => <option key={y} value={y}>{y}</option>)}
              </select>
            </label>
          )}
          </>
          )}
        </nav>

        {/* -- the pane ------------------------------------------------------------------- */}
        <div className="rulepane" ref={paneRef}>
          <div className="rulepanehd">
            <h2>
              {/*
                THE GRID IS NOT A TOPIC. It is one rule drawn as a table and carries no topics at
                all, so with a filter engaged it narrows nothing and the pane is showing every
                rule of that status — which is the right answer and the wrong heading. Standing
                on a topicless group under a filter, the pane says what it is actually showing.
              */}
              {isSearch ? 'Search' : order === 'year'
                ? (year === 'all' ? 'Every year' : String(year))
                : filtered && current.topics.length === 0 ? 'Every topic'
                  : current.label}
            </h2>
            <p className="mut">
              {isSearch
                ? `${shown.length} across all ${counts.all}`
                : filtered
                  ? `${shown.length} ${STATUS_FILTERS.find((f) => f.id === status)!.label
                    .toLowerCase()}`
                  : current.kind === 'grid'
                    ? '2019-II-1, as a table'
                    : year === 'all'
                      ? `${inForce.length} in force`
                      : `${shown.length} of ${inForce.length} in force · ${year}`}
            </p>
          </div>

          {/*
            The Grid and the Scoring table are ONE RULE DRAWN AS A TABLE, not lists — there is no
            such thing as a superseded view of them — so they stand down the moment the reader is
            asking a question a table cannot answer.
          */}
          {!filtered && current.kind === 'grid' && <GridPane role={role} store={store} />}

          {!filtered && current.kind === 'scoring' && <ScoringPane />}

          {/*
            `=== 'list'`, not `!== 'grid'`. Scoring is its own kind and renders its seven rules
            inside its pane, beneath the table they explain; the looser test drew them twice and
            the badge read 7 against 14 rows.
          */}
          {(filtered || current.kind === 'list') && (
            <Linkify>
              <ul className="rulerows">
                {shown.map((r) => (
                  <RuleRow
                    key={r.id} rule={r} open={openRule === r.id}
                    onToggle={() => setOpenRule((v) => (v === r.id ? null : r.id))}
                    onOpenRule={(id) => {
                      const owner = groupOwning(id);
                      if (owner) setGroup(owner.id);
                      setYear('all');
                      setOpenRule(id);
                    }}
                    interps={byRule.get(r.id) ?? []}
                    panelInput={panelInput} role={role} gm={gm} store={store}
                    asking={asking === r.id} question={question}
                    onAsk={() => setAsking(r.id)}
                    onQuestion={setQuestion}
                    onCancelAsk={() => { setAsking(null); setQuestion(''); }}
                    onRaised={() => { setAsking(null); setQuestion(''); }}
                  />
                ))}
                {retiredRow && !shown.some((r) => r.id === retiredRow.id) && (
                  <RuleRow
                    key={retiredRow.id} rule={retiredRow} open
                    onToggle={() => setOpenRule(null)}
                    onOpenRule={(id) => { setOpenRule(id); }}
                    interps={byRule.get(retiredRow.id) ?? []}
                    panelInput={panelInput} role={role} gm={gm} store={store}
                    asking={asking === retiredRow.id} question={question}
                    onAsk={() => setAsking(retiredRow.id)}
                    onQuestion={setQuestion}
                    onCancelAsk={() => { setAsking(null); setQuestion(''); }}
                    onRaised={() => { setAsking(null); setQuestion(''); }}
                  />
                )}
                {/*
                  AN EMPTY PANE IS AN ANSWER. A filter and a topic that meet on nothing is a real
                  reply to a real question — no trades rule has ever been rescinded — and falling
                  through to a bare tail turns that into a screen that looks broken.
                */}
                {shown.length === 0 && !retiredRow && empty && (
                  <li className="ruleempty">
                    <b>{empty.title}</b>
                    <span className="mut">{empty.body}</span>
                  </li>
                )}
                {shown.length === 0 && !retiredRow && !empty && (
                  <li className="note">
                    No rule in force here for {String(year)}.{' '}
                    <button type="button" className="linkbtn" onClick={() => setYear('all')}>
                      Show every year
                    </button>
                  </li>
                )}
              </ul>
            </Linkify>
          )}

          {commish && !isSearch && current.kind === 'list' && (
            <p className="note tight mut">
              Rule text is the Competition Committee’s to change — see League › Proposals.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

function RailRow(
  { group, on, plain = false, count: given, onPick }:
  { group: RuleGroup; on: boolean; plain?: boolean; count?: number; onPick: () => void },
) {
  /*
   * The Grid is a table and has no list, so it shows no count rather than showing a zero.
   *
   * `given` is the count UNDER THE LIVE STATUS FILTER, passed in rather than recomputed: the
   * badge and the pane have to be the same number, which is the whole argument of `ruleGroups`.
   */
  const count = group.kind === 'grid' ? null : (given ?? countIn(group.id));
  return (
    <li>
      <button type="button"
        className={`railrow ${plain ? 'plain' : ''} ${on ? 'on' : ''} `
          + `${count === 0 ? 'none' : ''}`}
        aria-current={on ? 'true' : undefined} onClick={onPick}>
        {group.hue !== null && (
          <span className="raildot" aria-hidden="true"
            style={{ background: `hsl(${group.hue} 58% 44%)` }} />
        )}
        <span className="railtext">
          <b>{group.label}</b>
          {!plain && <span className="railblurb">{group.blurb}</span>}
        </span>
        {count !== null && <span className="railcount">{count}</span>}
      </button>
    </li>
  );
}

interface RowProps {
  rule: RulebookEntry;
  open: boolean;
  onToggle: () => void;
  onOpenRule: (id: string) => void;
  interps: InterpretationDoc[];
  panelInput: Parameters<typeof InterpretationPanel>[0]['panelInput'];
  role: Role;
  gm: string;
  store: LeagueStore;
  asking: boolean;
  question: string;
  onAsk: () => void;
  onQuestion: (q: string) => void;
  onCancelAsk: () => void;
  onRaised: () => void;
}

function RuleRow(props: RowProps) {
  const { rule: r, open, onToggle, onOpenRule, interps, panelInput, role, gm, store } = props;
  const line = lineageOf(r.id);

  return (
    /*
     * THE ROW CARRIES THE RULE ID. This is the anchor twenty `§` marks resolve to, and
     * `scroll-margin-top` in the stylesheet is what stops the sticky header landing on top of it.
     */
    <li id={r.id} className={`rulerow ${r.status} ${open ? 'on' : ''}`}>
      <button type="button" className="rulerowhd" aria-expanded={open} onClick={onToggle}
        data-nolink>
        <span className="ridchip">{r.id}</span>
        <span className="rtitle">{r.title}</span>
        {r.status !== 'active' && <span className="rchip retired">{r.status}</span>}
        {r.provisional && <span className="rchip prov">provisional</span>}
        <span className="rplus" aria-hidden="true">{open ? '−' : '+'}</span>
      </button>

      <p className="rsummary">{r.summary}</p>

      {/* Straight from `supersedes` — the design pass hardcoded one of these to an id that does
          not exist, which is why nothing here is written by hand. */}
      {line.replaces.length > 0 && (
        <p className="rlineage">
          Replaces{' '}
          {line.replaces.map((x, i) => (
            <span key={x.id}>
              {i > 0 && ', '}
              <button type="button" className="ridlink" onClick={() => onOpenRule(x.id)}>
                {x.id}
              </button>
            </span>
          ))}
        </p>
      )}
      {line.replacedBy.length > 0 && (
        <p className="rlineage retired">
          Replaced by{' '}
          {line.replacedBy.map((x, i) => (
            <span key={x.id}>
              {i > 0 && ', '}
              <button type="button" className="ridlink" onClick={() => onOpenRule(x.id)}>
                {x.id}
              </button>
            </span>
          ))}
        </p>
      )}

      {open && (
        <div className="rulerowbody">
          {/* Verbatim, and `pre-line` because the rulebook's own line breaks carry its structure. */}
          <div className="rverbatim">{r.text}</div>

          {interps.map((i) => (
            <InterpretationPanel
              key={i.id}
              interp={i}
              panelInput={panelInput}
              role={role}
              onAnswer={(who, answer) => void store.saveInterpretation({
                ...i, answers: { ...(i.answers ?? {}), [who]: answer },
                updatedAt: new Date().toISOString(),
              })}
              onRecord={(decision) => {
                const now = new Date().toISOString();
                void store.saveInterpretation({
                  ...i, decision, decidedAt: now, decidedBy: gm, updatedAt: now,
                });
                /*
                 * THEN CODIFIED, and it happens here rather than being promised on a card. The
                 * panel answered for a season; the rule text is the committee's job, so recording
                 * an interpretation also writes a DRAFT proposal against next year.
                 *
                 * This is the only path in the app that creates one. It moved with the page rather
                 * than being left behind in the old Rulebook — without it Proposals loses its
                 * inbound feed from interpretations entirely.
                 */
                void store.saveProposal({ ...codifyDraft(i, gm, now), id: `codify-${i.id}` });
              }}
            />
          ))}

          {/* ANY MANAGER MAY RAISE ONE, and the database agrees — see `firestore.rules`. Making
              somebody find a commissioner before he can even ASK reintroduces the delay a
              three-man panel exists to remove. */}
          {props.asking ? (
            <div className="askrule">
              <input className="search" value={props.question} autoFocus
                placeholder="What is unclear about this rule?"
                onChange={(e) => props.onQuestion(e.target.value)} />
              <button type="button" className="linkbtn" onClick={props.onCancelAsk}>cancel</button>
              <button type="button" className="btn" disabled={!props.question.trim()}
                onClick={() => {
                  const now = new Date().toISOString();
                  void store.saveInterpretation({
                    id: `${r.id}-${Date.now().toString(36)}`,
                    season: SEASON, ruleId: r.id, question: props.question.trim(),
                    raisedBy: gm, raisedAt: now, updatedAt: now,
                  });
                  props.onRaised();
                }}>Raise it with the panel</button>
            </div>
          ) : (
            <button type="button" className="linkbtn tiny" onClick={props.onAsk}>
              raise an interpretation
            </button>
          )}
        </div>
      )}
    </li>
  );
}
